import { vec3 } from 'gl-matrix';
import type { Vector3 } from '@kitware/vtk.js/types';

/**
 * Math utilities for dental arch CPR.
 *
 * The curve is a centripetal Catmull-Rom spline through user-placed control
 * points in world space. We expose:
 *   - polyline sampling (`interpolateCatmullRom`)
 *   - arc-length reparameterization (`buildArcLengthLUT`, `pointAtArc`)
 *   - per-arc Frenet-like frames with up-stabilized normal
 *     (`buildArcFrames` / `sampleFrameAtArc`) suited to a dental volume where
 *     the curve lies (roughly) in the axial plane and "up" should remain the
 *     volume's Superior LPS axis so cross-sections look anatomically correct.
 *
 * Coordinates everywhere are LPS world-space.
 */

const EPS = 1e-8;

export interface ArcFrame {
  /** world-space point on the curve */
  point: Vector3;
  /** unit tangent (direction of increasing arc length) */
  tangent: Vector3;
  /**
   * "right" axis in the cross-section plane (perpendicular to tangent).
   * For dental CBCT this maps to the lingual / buccal direction.
   */
  right: Vector3;
  /** "up" axis (≈ superior in the volume frame) */
  up: Vector3;
  /** arc length from the start of the curve to this point */
  arc: number;
}

/* ------------------------------------------------------------------------- */
/* Catmull-Rom interpolation                                                 */
/* ------------------------------------------------------------------------- */

/**
 * Interpolate a centripetal Catmull-Rom spline through the given points.
 *
 * Returns a dense polyline. Endpoints are duplicated so the curve passes
 * through the first and last control points. With < 2 input points the result
 * is the input itself (no interpolation possible).
 *
 * `samplesPerSegment` controls smoothness; ~20-40 is plenty for an arch.
 */
export function interpolateCatmullRom(
  points: Vector3[],
  samplesPerSegment = 24
): Vector3[] {
  if (points.length < 2) return points.map((p) => [p[0], p[1], p[2]]);

  // Pad the array so the first/last segments also have neighbors.
  const pad: Vector3[] = [points[0], ...points, points[points.length - 1]];

  const out: Vector3[] = [];

  for (let i = 1; i < pad.length - 2; i++) {
    const p0 = pad[i - 1];
    const p1 = pad[i];
    const p2 = pad[i + 1];
    const p3 = pad[i + 2];

    // Centripetal knot parameterization (alpha = 0.5).
    const t0 = 0;
    const t1 = t0 + Math.pow(distance(p0, p1), 0.5);
    const t2 = t1 + Math.pow(distance(p1, p2), 0.5);
    const t3 = t2 + Math.pow(distance(p2, p3), 0.5);

    const segments =
      i === pad.length - 3 ? samplesPerSegment + 1 : samplesPerSegment;
    for (let s = 0; s < segments; s++) {
      const t = t1 + ((t2 - t1) * s) / samplesPerSegment;
      out.push(catmullRomPoint(p0, p1, p2, p3, t0, t1, t2, t3, t));
    }
  }

  return out;
}

function catmullRomPoint(
  p0: Vector3,
  p1: Vector3,
  p2: Vector3,
  p3: Vector3,
  t0: number,
  t1: number,
  t2: number,
  t3: number,
  t: number
): Vector3 {
  // Guard against duplicate control points (zero-length parameter spans).
  const d10 = Math.max(t1 - t0, EPS);
  const d21 = Math.max(t2 - t1, EPS);
  const d32 = Math.max(t3 - t2, EPS);
  const d20 = Math.max(t2 - t0, EPS);
  const d31 = Math.max(t3 - t1, EPS);

  const a1 = lerp(p0, p1, (t - t0) / d10);
  const a2 = lerp(p1, p2, (t - t1) / d21);
  const a3 = lerp(p2, p3, (t - t2) / d32);

  const b1 = lerp(a1, a2, (t - t0) / d20);
  const b2 = lerp(a2, a3, (t - t1) / d31);

  return lerp(b1, b2, (t - t1) / d21);
}

function lerp(a: Vector3, b: Vector3, t: number): Vector3 {
  return [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
    a[2] + (b[2] - a[2]) * t,
  ];
}

function distance(a: Vector3, b: Vector3) {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  const dz = a[2] - b[2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/* ------------------------------------------------------------------------- */
/* Arc length                                                                */
/* ------------------------------------------------------------------------- */

export interface ArcLengthLUT {
  /** Cumulative arc length at each polyline vertex (in mm). */
  lengths: number[];
  /** Total arc length in mm. */
  totalLength: number;
}

export function buildArcLengthLUT(polyline: Vector3[]): ArcLengthLUT {
  const lengths: number[] = new Array(polyline.length).fill(0);
  let total = 0;
  for (let i = 1; i < polyline.length; i++) {
    total += distance(polyline[i - 1], polyline[i]);
    lengths[i] = total;
  }
  return { lengths, totalLength: total };
}

/**
 * Sample the polyline at a given arc-length parameter (in mm).
 *
 * Returns the world position and the index `i` such that
 * `lengths[i] <= s <= lengths[i+1]`. The caller can use that index to look
 * up neighboring points without re-binary-searching.
 */
export function pointAtArc(
  polyline: Vector3[],
  lut: ArcLengthLUT,
  s: number
): { point: Vector3; segIndex: number; t: number } {
  if (polyline.length === 0) {
    return { point: [0, 0, 0], segIndex: 0, t: 0 };
  }
  if (polyline.length === 1 || s <= 0) {
    return { point: [...polyline[0]] as Vector3, segIndex: 0, t: 0 };
  }
  if (s >= lut.totalLength) {
    const last = polyline.length - 1;
    return { point: [...polyline[last]] as Vector3, segIndex: last - 1, t: 1 };
  }

  // Binary-search for the segment containing arc length `s`.
  let lo = 0;
  let hi = lut.lengths.length - 1;
  while (lo + 1 < hi) {
    const mid = (lo + hi) >>> 1;
    if (lut.lengths[mid] <= s) lo = mid;
    else hi = mid;
  }
  const segIndex = lo;
  const segLen = lut.lengths[segIndex + 1] - lut.lengths[segIndex];
  const t = segLen > EPS ? (s - lut.lengths[segIndex]) / segLen : 0;
  return {
    point: lerp(polyline[segIndex], polyline[segIndex + 1], t),
    segIndex,
    t,
  };
}

/* ------------------------------------------------------------------------- */
/* Frames                                                                    */
/* ------------------------------------------------------------------------- */

/**
 * Compute the unit tangent on a polyline at the given segment index.
 * Uses central differences for interior points and a one-sided difference at
 * the ends. This is much more stable than the per-segment direction when the
 * curve is irregular.
 */
function tangentAt(polyline: Vector3[], i: number): Vector3 {
  const n = polyline.length;
  const a = polyline[Math.max(0, i - 1)];
  const b = polyline[Math.min(n - 1, i + 1)];
  const t: Vector3 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
  const len = Math.hypot(t[0], t[1], t[2]);
  if (len < EPS) return [1, 0, 0];
  return [t[0] / len, t[1] / len, t[2] / len];
}

/**
 * Build per-sample Frenet-like frames with the `up` axis locked against a
 * stable global up vector (typically the volume's Superior LPS axis).
 *
 * This avoids the Frenet flip problem you get with curvature-based normals on
 * inflecting curves like a dental arch. The cross-section plane is spanned by
 * (right, up); the panoramic image is unrolled along (arc, up).
 */
export function buildArcFrames(
  polyline: Vector3[],
  lut: ArcLengthLUT,
  globalUp: Vector3
): ArcFrame[] {
  const frames: ArcFrame[] = [];
  for (let i = 0; i < polyline.length; i++) {
    const tangent = tangentAt(polyline, i);
    const { right, up } = stabilizedFrame(tangent, globalUp);
    frames.push({
      point: [...polyline[i]] as Vector3,
      tangent,
      right,
      up,
      arc: lut.lengths[i],
    });
  }
  return frames;
}

/**
 * Compute (right, up) from a tangent and a desired global up vector.
 *
 * - `right = normalize(globalUp x tangent)` — perpendicular to both, lying in
 *   the plane perpendicular to the tangent.
 * - `up = normalize(tangent x right)` — guaranteed perpendicular to tangent
 *   and as close to `globalUp` as possible.
 *
 * Falls back gracefully when the tangent is parallel to globalUp.
 */
function stabilizedFrame(
  tangent: Vector3,
  globalUp: Vector3
): { right: Vector3; up: Vector3 } {
  const t = vec3.fromValues(tangent[0], tangent[1], tangent[2]);
  const g = vec3.fromValues(globalUp[0], globalUp[1], globalUp[2]);

  const right = vec3.create();
  vec3.cross(right, g, t);
  let rLen = vec3.length(right);

  // Tangent parallel to globalUp: choose any perpendicular as a fallback.
  if (rLen < EPS) {
    const alt =
      Math.abs(t[0]) < 0.9
        ? vec3.fromValues(1, 0, 0)
        : vec3.fromValues(0, 1, 0);
    vec3.cross(right, alt, t);
    rLen = vec3.length(right);
  }
  vec3.scale(right, right, 1 / rLen);

  const up = vec3.create();
  vec3.cross(up, t, right);
  vec3.normalize(up, up);

  return {
    right: [right[0], right[1], right[2]],
    up: [up[0], up[1], up[2]],
  };
}

/**
 * Linearly interpolate a frame at arbitrary arc length.
 *
 * Spherical interpolation isn't necessary here because adjacent frames are
 * very close on a smooth arch, and we re-orthonormalize after lerping.
 */
export function sampleFrameAtArc(
  frames: ArcFrame[],
  lut: ArcLengthLUT,
  s: number,
  globalUp: Vector3
): ArcFrame {
  if (frames.length === 0) {
    return {
      point: [0, 0, 0],
      tangent: [1, 0, 0],
      right: [0, 1, 0],
      up: [0, 0, 1],
      arc: 0,
    };
  }
  if (frames.length === 1 || s <= 0) {
    return { ...frames[0], point: [...frames[0].point] as Vector3 };
  }
  if (s >= lut.totalLength) {
    const last = frames[frames.length - 1];
    return { ...last, point: [...last.point] as Vector3 };
  }

  let lo = 0;
  let hi = lut.lengths.length - 1;
  while (lo + 1 < hi) {
    const mid = (lo + hi) >>> 1;
    if (lut.lengths[mid] <= s) lo = mid;
    else hi = mid;
  }
  const a = frames[lo];
  const b = frames[lo + 1];
  const segLen = lut.lengths[lo + 1] - lut.lengths[lo];
  const t = segLen > EPS ? (s - lut.lengths[lo]) / segLen : 0;

  const point = lerp(a.point, b.point, t);
  const tan = normalizeVec(lerp(a.tangent, b.tangent, t));
  const { right, up } = stabilizedFrame(tan, globalUp);
  return { point, tangent: tan, right, up, arc: s };
}

function normalizeVec(v: Vector3): Vector3 {
  const len = Math.hypot(v[0], v[1], v[2]);
  if (len < EPS) return [1, 0, 0];
  return [v[0] / len, v[1] / len, v[2] / len];
}

/**
 * Offset each frame point along its `right` axis by `offsetMm` (negative =
 * inner / lingual side, positive = outer / buccal side).
 */
export function offsetPolylineAlongRight(
  frames: ArcFrame[],
  offsetMm: number
): Vector3[] {
  return frames.map((f) => [
    f.point[0] + f.right[0] * offsetMm,
    f.point[1] + f.right[1] * offsetMm,
    f.point[2] + f.right[2] * offsetMm,
  ]);
}
